import CustomModelLoader from '../models/custom-model-loader.js';
import CustomModelDialog from './custom-model-dialog.js';

class CatalogPanel {
  constructor(furnitureController) {
    this.furnitureController = furnitureController;
    this.container = null;
    this.searchInput = null;
    this.categoriesContainer = null;
    
    this.currentFilter = '';
    this.expandedCategories = new Set();
    
    // Riferimento all'InputController per disabilitare i comandi tastiera
    this.inputController = null;

    // Riferimento al DragDropManager per il touch drag
    this.dragDropManager = null;
    
    // Custom model loader
    this.customModelLoader = new CustomModelLoader();
    this.customModelDialog = null;
  }
  
  async init() {
    // Carica catalogo
    await this.furnitureController.loadCatalog();
    
    // Setup custom model loader
    this.furnitureController.setCustomModelLoader(this.customModelLoader);
    
    // Setup dialog
    this.customModelDialog = new CustomModelDialog(async (file, metadata) => {
      await this.handleCustomModelUpload(file, metadata);
    });
    
    // Crea UI
    this.createPanel();
    this.renderCatalog();
    
    console.log('✓ Pannello catalogo inizializzato');
  }
  
  async handleCustomModelUpload(file, metadata) {
    try {
      const customModel = await this.customModelLoader.loadCustomModel(file, metadata);
      console.log('✓ Modello custom aggiunto al catalogo');
      
      // Espande automaticamente la categoria "I Miei Modelli"
      this.expandedCategories.add('custom-models');
      
      // Ri-renderizza il catalogo
      this.renderCatalog();
    } catch (error) {
      console.error('Errore caricamento modello custom:', error);
      throw error;
    }
  }
  
  setInputController(inputController) {
    this.inputController = inputController;
  }

  setDragDropManager(ddm) {
    this.dragDropManager = ddm;
  }
  
  createPanel() {
    // Container principale
    this.container = document.createElement('div');
    this.container.id = 'catalog-panel';
    
    // Header
    const header = document.createElement('div');
    header.className = 'catalog-header';
    
    // Bottone aggiungi modello
    const addModelBtn = document.createElement('button');
    addModelBtn.textContent = '+ Aggiungi Modello';
    addModelBtn.className = 'add-model-button';
    
    addModelBtn.addEventListener('click', () => {
      this.customModelDialog.show();
    });
    
    header.appendChild(addModelBtn);    
    
    // Search bar
    this.searchInput = document.createElement('input');
    this.searchInput.type = 'text';
    this.searchInput.placeholder = '🔍 Cerca...';
    this.searchInput.className = 'catalog-search';
    
    this.searchInput.addEventListener('focus', () => {
      // Disabilita comandi tastiera quando si scrive nel campo di ricerca
      if (this.inputController) {
        this.inputController.disableKeyboard();
      }
    });
    
    this.searchInput.addEventListener('blur', () => {
      // Riabilita comandi tastiera quando si esce dal campo di ricerca
      if (this.inputController) {
        this.inputController.enableKeyboard();
      }
    });
    
    this.searchInput.addEventListener('input', (e) => {
      const previousFilter = this.currentFilter;
      this.currentFilter = e.target.value.toLowerCase();
      
      // Se la ricerca viene cancellata (da qualcosa a vuoto), chiude tutti i sottomenù
      if (previousFilter !== '' && this.currentFilter === '') {
        this.expandedCategories.clear();
      }
      
      this.renderCatalog();
    });
    
    header.appendChild(this.searchInput);
    
    // Container categorie
    this.categoriesContainer = document.createElement('div');
    this.categoriesContainer.className = 'categories-container';
    
    this.container.appendChild(header);
    this.container.appendChild(this.categoriesContainer);

    // Bottone collapse dinamico, ancorato al bordo sinistro del pannello
    const collapseBtn = document.createElement('button');
    collapseBtn.id = 'catalog-panel-toggle';
    collapseBtn.className = 'panel-toggle-btn panel-toggle-btn--right';
    collapseBtn.innerHTML = '&#8250;'; // ›
    collapseBtn.title = 'Nascondi catalogo';
    document.body.appendChild(collapseBtn);

    const positionCatalogToggle = () => {
      const rect = this.container.getBoundingClientRect();
      const collapsed = this.container.classList.contains('collapsed');
      collapseBtn.style.top   = (rect.top + rect.height / 2) + 'px';
      const rightVal = window.innerWidth - rect.left;
      collapseBtn.style.right = (collapsed && rect.left >= window.innerWidth)
        ? '0px'
        : Math.max(0, rightVal) + 'px';
    };

    const TRANSITION_MS = 310;
    const animateCatalogToggle = () => {
      const start = performance.now();
      const tick = (now) => {
        positionCatalogToggle();
        if (now - start < TRANSITION_MS) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    let catalogCollapsed = false;
    const updateCatalogToggle = () => {
      catalogCollapsed = !catalogCollapsed;
      this.container.classList.toggle('collapsed', catalogCollapsed);
      collapseBtn.innerHTML = catalogCollapsed ? '&#8249;' : '&#8250;';
      collapseBtn.title     = catalogCollapsed ? 'Mostra catalogo' : 'Nascondi catalogo';
      animateCatalogToggle();
    };

    collapseBtn.addEventListener('click', updateCatalogToggle);

    requestAnimationFrame(positionCatalogToggle);
    window.addEventListener('resize', positionCatalogToggle);

    document.body.appendChild(this.container);
  }
  
  renderCatalog() {
    this.categoriesContainer.innerHTML = '';
    
    const catalog = this.furnitureController.getCatalog();
    
    // Prima categoria: I Miei Modelli (custom)
    const customModels = this.customModelLoader.getCustomModels();
    if (customModels.length > 0) {
      const customCategory = {
        id: 'custom-models',
        name: 'I Miei Modelli',
        icon: '📦',
        items: customModels
      };
      
      const categoryEl = this.createCategoryElement(customCategory, true);
      if (categoryEl) {
        this.categoriesContainer.appendChild(categoryEl);
      }
    }
    
    // Poi le categorie standard
    if (catalog) {
      catalog.categories.forEach(category => {
        const categoryEl = this.createCategoryElement(category, false);
        if (categoryEl) {
          this.categoriesContainer.appendChild(categoryEl);
        }
      });
    }
  }
  
  createCategoryElement(category, isCustom = false) {
    // Filtra items in base alla ricerca
    const filteredItems = category.items.filter(item => 
      this.currentFilter === '' || 
      item.name.toLowerCase().includes(this.currentFilter) ||
      category.name.toLowerCase().includes(this.currentFilter)
    );
    
    if (filteredItems.length === 0 && this.currentFilter !== '') {
      return null;
    }
    
    // Container categoria
    const categoryContainer = document.createElement('div');
    categoryContainer.className = 'category-container';
    
    // Header categoria
    const categoryHeader = document.createElement('div');
    categoryHeader.className = isCustom ? 'category-header custom' : 'category-header';
    
    const categoryTitle = document.createElement('div');
    categoryTitle.className = 'category-title';
    
    const icon = document.createElement('span');
    icon.className = 'category-icon';
    icon.textContent = category.icon;
    
    const name = document.createElement('span');
    name.textContent = category.name;
    
    const count = document.createElement('span');
    count.className = 'category-count';
    count.textContent = `(${filteredItems.length})`;
    
    categoryTitle.appendChild(icon);
    categoryTitle.appendChild(name);
    categoryTitle.appendChild(count);
    
    const arrow = document.createElement('span');
    arrow.className = 'category-arrow';
    arrow.textContent = this.expandedCategories.has(category.id) ? '▼' : '▶';
    
    categoryHeader.appendChild(categoryTitle);
    categoryHeader.appendChild(arrow);
    
    // Container oggetti
    const itemsContainer = document.createElement('div');
    itemsContainer.className = 'items-container';
    itemsContainer.style.display = this.expandedCategories.has(category.id) ? 'block' : 'none';
    
    // Toggle espandi/comprimi
    categoryHeader.addEventListener('click', () => {
      if (this.expandedCategories.has(category.id)) {
        this.expandedCategories.delete(category.id);
        itemsContainer.style.display = 'none';
        arrow.textContent = '▶';
      } else {
        this.expandedCategories.add(category.id);
        itemsContainer.style.display = 'block';
        arrow.textContent = '▼';
      }
    });
    
    // Renderizza oggetti
    filteredItems.forEach(item => {
      const itemEl = this.createItemElement(item);
      itemsContainer.appendChild(itemEl);
    });
    
    categoryContainer.appendChild(categoryHeader);
    categoryContainer.appendChild(itemsContainer);
    
    // Auto-espandi se c'è filtro attivo
    if (this.currentFilter !== '' && filteredItems.length > 0) {
      this.expandedCategories.add(category.id);
      itemsContainer.style.display = 'block';
      arrow.textContent = '▼';
    }
    
    return categoryContainer;
  }
  
  createItemElement(item) {
    const itemEl = document.createElement('div');
    itemEl.className = 'catalog-item';
    itemEl.draggable = true;
    itemEl.dataset.itemId = item.id;
    
    // Thumbnail
    const thumbnail = document.createElement('div');
    thumbnail.className = 'item-thumbnail';
    
    // Se è un modello custom con thumbnail generata
    if (item.isCustom && item.thumbnail) {
      const img = document.createElement('img');
      img.src = item.thumbnail; // Data URL generata
      thumbnail.appendChild(img);
      
      // Bottone elimina per modelli custom
      const deleteBtn = document.createElement('button');
      deleteBtn.textContent = '×';
      deleteBtn.className = 'delete-model-button';
      
      deleteBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.handleCustomModelDeletion(item);
      });
      
      thumbnail.appendChild(deleteBtn);
    }
    // Se c'è thumbnail standard
    else if (item.thumbnail) {
      const img = document.createElement('img');
      img.src = `/assets/thumbnails/${item.thumbnail}`;
      img.onerror = () => {
        // Fallback se immagine non trovata
        const fallback = document.createElement('span');
        fallback.className = 'fallback-icon';
        fallback.textContent = item.type === 'door' ? '🚪' : item.type === 'window' ? '🪟' : '📦';
        thumbnail.innerHTML = '';
        thumbnail.appendChild(fallback);
      };
      thumbnail.appendChild(img);
    } else {
      const fallback = document.createElement('span');
      fallback.className = 'fallback-icon';
      fallback.textContent = '📦';
      thumbnail.appendChild(fallback);
    }
    
    // Nome
    const name = document.createElement('div');
    name.className = 'item-name';
    name.textContent = item.name;
    
    // Dimensioni
    const dims = document.createElement('div');
    dims.className = 'item-dimensions';
    dims.textContent = `${item.dimensions.width}×${item.dimensions.depth}×${item.dimensions.height}m`;
    
    itemEl.appendChild(thumbnail);
    itemEl.appendChild(name);
    itemEl.appendChild(dims);
    
    // Impedisce menu contestuale (tasto destro / pressione prolungata) sulla thumbnail
    thumbnail.addEventListener('contextmenu', (e) => e.preventDefault());

    // MOUSE DRAG
    itemEl.addEventListener('dragstart', (e) => {
      // Salva l'ID direttamente sul manager prima di setData:
      // Brave blocca dataTransfer.getData() durante dragover, quindi
      // onDragOver non riceverebbe mai l'itemId senza questo workaround
      if (this.dragDropManager) this.dragDropManager.setDraggedItem(item.id);

      // Effetto "lift": scala, ombra marcata, opacità ridotta sull'item
      itemEl.style.transition = 'none';
      itemEl.style.opacity    = '0.55';
      itemEl.style.transform  = 'scale(1.05)';
      itemEl.style.boxShadow  = '0 10px 28px rgba(0,0,0,0.55)';

      // Riduce l'opacità dell'intero pannello catalogo
      if (this.container) this.container.style.opacity = '0.5';

      e.dataTransfer.effectAllowed = 'copy';
      e.dataTransfer.setData('itemId', item.id);
      console.log('Drag iniziato:', item.name);
    });

    itemEl.addEventListener('dragend', () => {
      // Ripristina l'item
      itemEl.style.transition = '';
      itemEl.style.opacity    = '';
      itemEl.style.transform  = '';
      itemEl.style.boxShadow  = '';

      // Ripristina il pannello
      if (this.container) this.container.style.opacity = '';

      // Pulisce il riferimento sul manager (per sicurezza, cleanup() lo fa già)
      if (this.dragDropManager) this.dragDropManager.setDraggedItem(null);
    });

    // TOUCH LONG-PRESS DRAG
    // Il drag touch parte solo dopo una pressione prolungata (500 ms)
    // Se il dito si muove prima dello scatto, il long-press viene annullato
    let _lpTimer   = null;
    let _lpMoved   = false;
    let _lpStartX  = 0;
    let _lpStartY  = 0;
    const LP_DELAY = 500;  // ms
    const LP_SLOP  = 8;    // px – tolleranza movimento prima di annullare

    const _lpCancel = () => {
      clearTimeout(_lpTimer);
      _lpTimer = null;
    };

    itemEl.addEventListener('touchstart', (e) => {
      _lpMoved  = false;
      _lpStartX = e.changedTouches[0].clientX;
      _lpStartY = e.changedTouches[0].clientY;

      _lpTimer = setTimeout(() => {
        if (_lpMoved) return;

        // Feedback aptico
        if (navigator.vibrate) navigator.vibrate(50);

        // Lift effect sull'item
        itemEl.style.transition = 'none';
        itemEl.style.opacity    = '0.55';
        itemEl.style.transform  = 'scale(1.05)';
        itemEl.style.boxShadow  = '0 10px 28px rgba(0,0,0,0.55)';

        // Riduce opacità pannello
        if (this.container) this.container.style.opacity = '0.5';

        // Blocca lo scroll del catalogo per tutta la durata del drag
        if (this.categoriesContainer) this.categoriesContainer.style.overflowY = 'hidden';

        // Avvia drag touch
        if (this.dragDropManager) {
          e._sourceElement = itemEl;
          this.dragDropManager.startTouchDrag(item.id, e);
        }

        // Ripristino al rilascio del dito
        const _lpRestore = () => {
          itemEl.style.transition = '';
          itemEl.style.opacity    = '';
          itemEl.style.transform  = '';
          itemEl.style.boxShadow  = '';
          if (this.container) this.container.style.opacity = '';
          // Ripristina lo scroll del catalogo
          if (this.categoriesContainer) this.categoriesContainer.style.overflowY = '';
          document.removeEventListener('touchend',    _lpRestore);
          document.removeEventListener('touchcancel', _lpRestore);
        };
        document.addEventListener('touchend',    _lpRestore, { once: true });
        document.addEventListener('touchcancel', _lpRestore, { once: true });

      }, LP_DELAY);
    }, { passive: true });

    itemEl.addEventListener('touchmove', (e) => {
      const dx = e.changedTouches[0].clientX - _lpStartX;
      const dy = e.changedTouches[0].clientY - _lpStartY;
      if (Math.sqrt(dx * dx + dy * dy) > LP_SLOP) {
        _lpMoved = true;
        _lpCancel();
      }
    }, { passive: true });

    itemEl.addEventListener('touchend',    _lpCancel, { passive: true });
    itemEl.addEventListener('touchcancel', _lpCancel, { passive: true });
    
    return itemEl;
  }
  
  hide() {
    if (this.container) {
      this.container.style.display = 'none';
    }
  }
  
  show() {
    if (this.container) {
      this.container.style.display = 'flex';
    }
  }
  
  toggle() {
    if (this.container) {
      if (this.container.style.display === 'none') {
        this.show();
      } else {
        this.hide();
      }
    }
  }  
  
  handleCustomModelDeletion(item) {
    // Conta quanti mobili di questo tipo ci sono nella scena
    const furnitureInScene = this.furnitureController.getAllFurniture().filter(
      f => f.catalogItem.id === item.id
    );
    
    const count = furnitureInScene.length;
    
    // Crea messaggio di conferma
    let confirmMessage;
    if (count === 0) {
      confirmMessage = `Eliminare "${item.name}" dal catalogo?`;
    } else if (count === 1) {
      confirmMessage = `Eliminare "${item.name}"?\n\n⚠️ Questo rimuoverà anche 1 oggetto presente nella scena.`;
    } else {
      confirmMessage = `Eliminare "${item.name}"?\n\n⚠️ Questo rimuoverà anche ${count} oggetti presenti nella scena.`;
    }
    
    if (confirm(confirmMessage)) {
      // Rimuove tutti i mobili di questo tipo dalla scena
      furnitureInScene.forEach(furniture => {
        this.furnitureController.removeFurniture(furniture.id);
      });
      
      // Rimuove dal catalogo
      this.customModelLoader.removeCustomModel(item.id);
      
      // Aggiorna UI
      this.renderCatalog();
      
      console.log(`✓ Modello "${item.name}" eliminato (${count} oggetti rimossi dalla scena)`);
    }
  }
  
  dispose() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    
    if (this.customModelLoader) {
      this.customModelLoader.dispose();
    }
    
    if (this.customModelDialog) {
      this.customModelDialog.dispose();
    }
  }
}

export default CatalogPanel;